import type { MerchantsOfferSummariesResource } from '../../../../../Entities/MerchantsOfferSummary';
import { removeUndefinedKeys } from '../../../../../utils';

export type MetricKey = keyof NonNullable<MerchantsOfferSummariesResource['attributes']>;
export type SortOrder = 'asc' | 'desc';

export interface SortState {
  metricKey: MetricKey;
  order: SortOrder;
}

interface OffersListFilter {
  any?: {
    merchant?: {
      id?: string[];
    };
  };
  equals?: {
    category?: {
      is_offer_comparison_allowed?: boolean;
    };
  };
  is_approved?: boolean;
  not?: {
    isNull?: {
      category?: boolean;
      product?: boolean;
    };
  };
}

const approvedBackgrounds = ['#dfeeda', '#cfe7c7', '#beddb3', '#acd29f'];
const disapprovedBackgrounds = ['#f3ddd8', '#eccac2', '#e4b7ad', '#dba398'];
const hoverBackgrounds = ['#edf4fb', '#d8e8f7', '#bfd8ee', '#a8c8e3'];

export function getMetricBackground(metricKey: MetricKey | undefined): string {
  if (!metricKey) {
    return 'background.paper';
  }

  const depth = metricDepth(metricKey);

  if (metricKey.startsWith('approved')) {
    return approvedBackgrounds[Math.min(depth, approvedBackgrounds.length - 1)];
  }

  if (metricKey.startsWith('disapproved')) {
    return disapprovedBackgrounds[Math.min(depth, disapprovedBackgrounds.length - 1)];
  }

  return 'background.paper';
}

export function getMetricHoverBackground(metricKey: MetricKey): string {
  return hoverBackgrounds[Math.min(metricDepth(metricKey), hoverBackgrounds.length - 1)];
}

export function getOffersSearch(
  metricKey: MetricKey,
  record?: MerchantsOfferSummariesResource,
): string {
  const merchantId =
    record?.id !== 'totals' ? record?.relationships?.merchant?.data?.id : undefined;
  const searchParams = new URLSearchParams({
    filter: JSON.stringify(getOffersFilter(metricKey, record)),
    displayedFilters: JSON.stringify(getDisplayedFilters(metricKey, merchantId)),
  });

  return `?${searchParams.toString()}`;
}

export function buildTotals(
  records: MerchantsOfferSummariesResource[],
): MerchantsOfferSummariesResource {
  const attributes = records.reduce<NonNullable<MerchantsOfferSummariesResource['attributes']>>(
    (totals, record) => {
      Object.entries(record.attributes ?? {}).forEach(([key, value]) => {
        if (typeof value === 'number') {
          const typedKey = key as keyof typeof totals;
          totals[typedKey] = (totals[typedKey] ?? 0) + value;
        }
      });
      return totals;
    },
    {},
  );

  return {
    id: 'totals',
    type: 'merchants_offer_summaries',
    attributes,
    relationships: {
      merchant: { data: null },
      region: { data: null },
      report: { data: null },
    },
  };
}

function metricDepth(metricKey?: MetricKey): number {
  if (!metricKey) {
    return 0;
  }

  if (metricKey.includes('combined')) {
    return 3;
  }

  if (metricKey.includes('comparable') || metricKey.includes('incomparable')) {
    return 2;
  }

  if (metricKey.includes('categorized')) {
    return 1;
  }

  return 0;
}

function getDisplayedFilters(metricKey: MetricKey, merchantId?: string): Record<string, boolean> {
  const isApproved = metricKey.startsWith('approved');
  const isDisapproved = metricKey.startsWith('disapproved');
  const isCategorized = metricKey.includes('categorized');
  const isComparable = metricKey.includes('comparable');
  const isIncomparable = metricKey.includes('incomparable');
  const isCombined = metricKey.includes('combined');

  return removeUndefinedKeys({
    'any.merchant.id': merchantId ? true : undefined,
    is_approved: isApproved || isDisapproved ? true : undefined,
    'not.isNull.category': isCategorized || isComparable || isIncomparable ? true : undefined,
    'equals.category.is_offer_comparison_allowed':
      isComparable || isIncomparable ? true : undefined,
    'not.isNull.product': isCombined ? true : undefined,
  });
}

function getOffersFilter(
  metricKey: MetricKey,
  record?: MerchantsOfferSummariesResource,
): OffersListFilter {
  const merchantId =
    record?.id !== 'totals' ? record?.relationships?.merchant?.data?.id : undefined;
  const filter: OffersListFilter = {};

  if (merchantId) {
    filter.any = { merchant: { id: [merchantId] } };
  }

  if (metricKey.startsWith('approved')) {
    filter.is_approved = true;
  } else if (metricKey.startsWith('disapproved')) {
    filter.is_approved = false;
  }

  if (metricKey.includes('not_categorized')) {
    filter.not = { isNull: { ...filter.not?.isNull, category: false } };
  } else if (
    metricKey.includes('categorized') ||
    metricKey.includes('comparable') ||
    metricKey.includes('incomparable')
  ) {
    filter.not = { isNull: { ...filter.not?.isNull, category: true } };
  }

  if (metricKey.includes('incomparable')) {
    filter.equals = { category: { is_offer_comparison_allowed: false } };
  } else if (metricKey.includes('comparable')) {
    filter.equals = { category: { is_offer_comparison_allowed: true } };
  }

  if (metricKey.includes('not_combined')) {
    filter.not = { isNull: { ...filter.not?.isNull, product: false } };
  } else if (metricKey.includes('combined')) {
    filter.not = { isNull: { ...filter.not?.isNull, product: true } };
  }

  return filter;
}
